import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import DataGrid, { Column, Editing, Lookup, RequiredRule, Button as GridButton, Toolbar, Item, SearchPanel, Form, Paging, Pager, Scrolling } from 'devextreme-react/data-grid';
import { Item as ItemF } from 'devextreme-react/form';
import { Button } from 'devextreme-react/button';
import { useApiMicrobiology } from '../../../api/api.microbiology';
import { SysI_MicrobiologiaMicroorganismosCatalogo } from '../../../types/types.microbiology';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { newToast } from '../../../components';
import { Popup, Tooltip } from 'devextreme-react';

const categoriaOptions = [
  { value: 'Bacteria', text: 'Bacteria' },
  { value: 'Hongo', text: 'Hongo' },
  { value: 'Virus', text: 'Virus' },
  { value: 'Parásito', text: 'Parásito' },
];

const gramTipoOptions = [
  { value: 'Positivo', text: 'Positivo' },
  { value: 'Negativo', text: 'Negativo' },
  { value: null, text: 'N/A' },
];

interface MicroorganismosCatalogoProps {
  dataMicroorganismos: SysI_MicrobiologiaMicroorganismosCatalogo[];
  update: () => void;
}

export const MicroorganismsCatalog: React.FC<MicroorganismosCatalogoProps> = ({ dataMicroorganismos, update }) => {
  const dataGridRef = useRef<DataGrid>(null);
  const { createMicroorganismo, updateMicroorganismo, deleteMicroorganismo, validarEliminacionMicroorganismo } = useApiMicrobiology();

  const handleRefresh = useCallback(() => {
    update();
  }, [update]);

  const handleDuplicate = useCallback((e: any) => {
    const rowData = e.row.data;
    if (!rowData) return;

    dataGridRef.current?.instance.addRow();

    setTimeout(() => {
      const visibleRows = dataGridRef.current?.instance.getVisibleRows();
      const newRowIndex = visibleRows?.findIndex((row: any) => row.isNewRow);

      if (newRowIndex !== -1 && newRowIndex !== undefined) {
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Moc_Nombre', `${rowData.Moc_Nombre} - Copia`);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Moc_NombreCientifico', rowData.Moc_NombreCientifico);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Moc_Categoria', rowData.Moc_Categoria);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Moc_GramTipo', rowData.Moc_GramTipo);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Moc_CodigoEstandar', rowData.Moc_CodigoEstandar);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Moc_CodigosSNOMED', rowData.Moc_CodigosSNOMED);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Moc_NotificacionObligatoria', rowData.Moc_NotificacionObligatoria);
        dataGridRef.current?.instance.cellValue(newRowIndex, 'Moc_Estado', true);
      }
    }, 100);

    newToast('INFO', 'Microorganismo duplicado. Complete la edición para guardar.', 3);
  }, []);

  const customDataStore = useMemo(() => {
    return new CustomStore({
      key: 'Moc_Id',
      load: async () => {
        return {
          data: dataMicroorganismos || [],
          totalCount: dataMicroorganismos?.length || 0,
        };
      },
      insert: async (values) => {
        try {
          const newMicroorganismo: SysI_MicrobiologiaMicroorganismosCatalogo = {
            Moc_Id: 0,
            Moc_Nombre: values.Moc_Nombre,
            Moc_NombreCientifico: values.Moc_NombreCientifico || '',
            Moc_Categoria: values.Moc_Categoria,
            Moc_GramTipo: values.Moc_GramTipo || 'N/A',
            Moc_CodigoEstandar: values.Moc_CodigoEstandar || '',
            Moc_CodigosSNOMED: values.Moc_CodigosSNOMED || '',
            Moc_NotificacionObligatoria: values.Moc_NotificacionObligatoria || false,
            Moc_Estado: true,
            Moc_FechaCreacion: new Date(),
          };

          await createMicroorganismo(newMicroorganismo);
          handleRefresh();
          return newMicroorganismo;
        } catch (error) {
          throw error;
        }
      },
      update: async (key, values) => {
        try {
          const currentItem = dataMicroorganismos?.find((item: any) => item.Moc_Id === key);

          const updatedMicroorganismo: SysI_MicrobiologiaMicroorganismosCatalogo = {
            ...currentItem,
            ...values,
          };

          await updateMicroorganismo(updatedMicroorganismo);
          handleRefresh();
        } catch (error) {
          throw error;
        }
      },
      remove: async (key) => {
        try {
          const validation = await validarEliminacionMicroorganismo(key);

          if (validation && !validation.CanDelete) {
            newToast('WARNING', validation.Message, 5);
            throw new Error(validation.Message);
          }

          await deleteMicroorganismo(key);
          handleRefresh();
        } catch (error) {
          throw error;
        }
      },
    });
  }, [dataMicroorganismos, handleRefresh]);

  const dataSource = useMemo(() => {
    return new DataSource({
      store: customDataStore,
      reshapeOnPush: true,
    });
  }, [customDataStore]);

  const onEditorPreparing = (e: any) => {
    if (e.dataField === 'Moc_NotificacionObligatoria' && e.parentType === 'dataRow') {
      e.editorName = 'dxCheckBox';
    }
    if (e.dataField === 'Moc_Estado' && e.parentType === 'dataRow') {
      e.editorName = 'dxCheckBox';
    }
  };

  const gramRender = (data: any) => {
    if (!data.value) return <span className='gram-badge na'>N/A</span>;
    return <span className={`gram-badge ${data.value.toLowerCase()}`}>{data.value}</span>;
  };

  return (
    <div className='microbiology-catalog-container'>
      <DataGrid ref={dataGridRef} dataSource={dataSource} height={'100%'} width={'100%'} showBorders={true} showRowLines={true} showColumnLines={false} rowAlternationEnabled={true} onEditorPreparing={onEditorPreparing} columnAutoWidth={false}>
        <Scrolling columnRenderingMode='standard' mode='standard' useNative={true} />
        <SearchPanel visible={true} width={300} placeholder='Buscar microorganismo...' />
        <Paging enabled={true} defaultPageSize={25} />
        <Pager visible={true} allowedPageSizes={[25, 50, 100, 200, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />

        <Editing mode='popup' allowAdding={true} allowUpdating={true} allowDeleting={true} useIcons={true}>
          <Popup title='Microorganismo' showTitle={true} width={700} height={550} >
            <Form labelLocation='top' colCount={2}>
              <ItemF dataField='Moc_Nombre' colSpan={2} />
              <ItemF dataField='Moc_NombreCientifico' colSpan={2} />
              <ItemF dataField='Moc_Categoria' />
              <ItemF dataField='Moc_GramTipo' />
              <ItemF dataField='Moc_CodigoEstandar' />
              <ItemF dataField='Moc_CodigosSNOMED' />
              <ItemF dataField='Moc_NotificacionObligatoria' />
              <ItemF dataField='Moc_Estado' />
            </Form>
          </Popup>
        </Editing>

        <Toolbar>
          <Item name='searchPanel' />
          <Item name='addRowButton' />
          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='RefrecarResultbc' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefresh} />
            <Tooltip target={'#RefrecarResultbc'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Refrescar datos
            </Tooltip>
          </Item>
        </Toolbar>

        <Column dataField='Moc_Id' caption='ID' width={80} alignment='center' allowEditing={false}></Column>

        <Column dataField='Moc_Nombre' caption='NOMBRE'>
          <RequiredRule message='El nombre es requerido' />
        </Column>

        <Column dataField='Moc_NombreCientifico' caption='NOMBRE CIENTÍFICO' />

        <Column dataField='Moc_Categoria' caption='CATEGORÍA' width={120}>
          <Lookup dataSource={categoriaOptions} valueExpr='value' displayExpr='text' />
          <RequiredRule message='La categoría es requerida' />
        </Column>

        <Column dataField='Moc_GramTipo' caption='GRAM' width={120} cellRender={gramRender}>
          <Lookup dataSource={gramTipoOptions} valueExpr='value' displayExpr='text' />
        </Column>

        <Column dataField='Moc_CodigoEstandar' caption='CÓDIGO' width={100} />

        <Column dataField='Moc_CodigosSNOMED' caption='SNOMED' width={120} visible={false} />

        <Column dataField='Moc_NotificacionObligatoria' caption='NOTIFICAR' width={100} alignment='center' />

        <Column dataField='Moc_Estado' caption='ESTADO' width={80} alignment='center' />

        <Column type='buttons' width={140} caption='ACCIONES'>
          <GridButton name='edit' />
          <GridButton name='delete' />
          <GridButton icon='copy' hint='Duplicar microorganismo' onClick={handleDuplicate} />
        </Column>
      </DataGrid>
    </div>
  );
};
